import { memo, type ReactNode } from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";

import { AuthDataProvider } from "./auth/auth-data-context";
import { AuthGuard } from "./auth/auth-guard";
import { AuthTokenProvider, useAuthToken } from "./auth/auth-token-context";
import { LoginPage } from "./auth/login";
import { VerifyPage } from "./auth/verify";
import { ErrorFallback } from "./error-fallback";
import { PartnersPortalRpcClientProvider } from "./rpc/client-context";
import { AppShell } from "./shell/app-shell";
import { StoreDetailPage } from "./stores/store-detail-page";
import { StoresPage } from "./stores/stores-page";

const RpcClientWithToken = memo(function RpcClientWithToken({
  children,
}: {
  children: ReactNode;
}) {
  const { token } = useAuthToken();
  return (
    <PartnersPortalRpcClientProvider authToken={token}>
      {children}
    </PartnersPortalRpcClientProvider>
  );
});

const RootLayout = memo(function RootLayout() {
  return (
    <AuthTokenProvider>
      <RpcClientWithToken>
        <AuthDataProvider>
          <Outlet />
        </AuthDataProvider>
      </RpcClientWithToken>
    </AuthTokenProvider>
  );
});

// Annotated because the inferred type names an internal react-router module.
export const router: ReturnType<typeof createBrowserRouter> =
  createBrowserRouter([
    {
      element: <RootLayout />,
      errorElement: <ErrorFallback />,
      children: [
        { index: true, element: <Navigate replace to="/stores" /> },
        { path: "/login", element: <LoginPage /> },
        { path: "/auth/verify", element: <VerifyPage /> },
        {
          element: <AuthGuard />,
          children: [
            {
              element: <AppShell />,
              children: [
                {
                  path: "/stores",
                  element: <StoresPage />,
                  handle: { breadcrumb: "Stores" },
                },
                { path: "/stores/:storeId", element: <StoreDetailPage /> },
              ],
            },
          ],
        },
        { path: "*", element: <Navigate replace to="/" /> },
      ],
    },
  ]);
